import { apiClient } from './api';
import {
  User,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  TwoFactorSetupResponse,
  TwoFactorVerifyRequest,
  TwoFactorBackupRequest,
  TwoFactorSetupRequest,
  TwoFactorDisableRequest,
  TwoFactorStatus,
} from '../types';
import { tokenStorage } from './token';

export const authService = {
  // Login
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const loginData = await apiClient.post<LoginResponse>('/auth/login', {
      email: credentials.email,
      password: credentials.password,
      rememberMe: credentials.rememberMe,
    });

    // Token is now stored in httpOnly cookie
    return loginData;
  },

  // Register
  register: async (userData: RegisterRequest): Promise<User> => {
    const response = await apiClient.post<{ user: User }>('/auth/register', {
      ...userData,
    } as Record<string, unknown>);
    return response.user;
  },

  // Logout
  logout: async (): Promise<void> => {
    try {
      await apiClient.post('/auth/logout');
    } finally {
      tokenStorage.removeToken();
      tokenStorage.removeRefreshToken();
    }
  },

  // Refresh token
  refreshToken: async (): Promise<string> => {
    const refreshToken = tokenStorage.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const tokenData = await apiClient.post<{ token: string }>('/auth/refresh', {
      refreshToken,
    });

    return tokenData.token;
  },

  // Get current user
  getCurrentUser: async (): Promise<User> => {
    return await apiClient.get<User>('/auth/me', {
      suppressErrorLog: true,
      suppressAuthRedirect: true,
    } as any);
  },

  // Update user profile
  updateProfile: async (userData: Partial<User>): Promise<User> => {
    return await apiClient.put<User>('/auth/profile', userData);
  },

  // Change password
  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    await apiClient.post('/auth/change-password', {
      currentPassword,
      newPassword,
    });
  },

  // Forgot password
  forgotPassword: async (email: string): Promise<void> => {
    await apiClient.post('/auth/forgot-password', { email });
  },

  // Reset password
  resetPassword: async (token: string, newPassword: string): Promise<void> => {
    await apiClient.post('/auth/reset-password', { token, newPassword });
  },

  // Validate token
  validateToken: async (token: string): Promise<boolean> => {
    try {
      await apiClient.post('/auth/validate', { token });
      return true;
    } catch {
      return false;
    }
  },

  // Get stored token
  getStoredToken: (): string | null => {
    return tokenStorage.getToken();
  },

  // Check if user is authenticated
  isAuthenticated: (): boolean => {
    const token = tokenStorage.getToken();
    return !!token;
  },

  // 2FA Methods

  // Get 2FA status for current user
  getTwoFactorStatus: async (): Promise<TwoFactorStatus> => {
    const response = await apiClient.get<TwoFactorStatus>('/auth/2fa/status');
    return response;
  },

  // Initiate 2FA setup
  setupTwoFactor: async (): Promise<TwoFactorSetupResponse> => {
    const response = await apiClient.post<TwoFactorSetupResponse>('/auth/2fa/setup');
    return response;
  },

  // Enable 2FA after verification
  enableTwoFactor: async (data: TwoFactorSetupRequest): Promise<void> => {
    await apiClient.post('/auth/2fa/enable', data as unknown as Record<string, unknown>);
  },

  // Verify 2FA during login
  verifyTwoFactor: async (data: TwoFactorVerifyRequest): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>(
      '/auth/2fa/verify',
      data as unknown as Record<string, unknown>,
    );

    // Token handled by cookies
    return response;
  },

  // Verify backup code during login
  verifyBackupCode: async (data: TwoFactorBackupRequest): Promise<LoginResponse> => {
    const response = await apiClient.post<LoginResponse>(
      '/auth/2fa/backup',
      data as unknown as Record<string, unknown>,
    );

    // Token handled by cookies
    return response;
  },

  // Disable 2FA
  disableTwoFactor: async (data: TwoFactorDisableRequest): Promise<void> => {
    await apiClient.post('/auth/2fa/disable', data as unknown as Record<string, unknown>);
  },
};
